import axios from "axios";
import { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { notificationActions } from "../../data/notification";
import useT from "../../i18n/useT";
import core from "../../core";

const Surveys = () => {
    const t = useT();
    const dispatch = useDispatch();
    const role = useSelector((state) => state.user.role);
    const isStaff = role === "admin" || role === "moderator";
    const isAdmin = role === "admin";

    const [view, setView] = useState("list"); // list | create | fill | results
    const [surveys, setSurveys] = useState([]);
    const [updates, setUpdates] = useState(0);

    const notify = (text, type) => {
        dispatch(notificationActions.changeNotification({ show: true, text, type }));
        setTimeout(() => dispatch(notificationActions.changeNotification({ show: false, text: "", type: "" })), 4000);
    };

    const loadList = useCallback(() => {
        axios.get("/api/surveys").then((res) => setSurveys(res.data.data || [])).catch(() => {});
    }, []);
    useEffect(() => { if (view === "list") loadList(); }, [view, updates, loadList]);

    // ---- create ----
    const [draft, setDraft] = useState({ title: "", description: "", questions: [{ text: "", type: "single", optionsStr: "" }] });
    const addQuestion = () => setDraft((d) => ({ ...d, questions: [...d.questions, { text: "", type: "single", optionsStr: "" }] }));
    const setQ = (i, patch) => setDraft((d) => ({ ...d, questions: d.questions.map((q, idx) => (idx === i ? { ...q, ...patch } : q)) }));
    const removeQ = (i) => setDraft((d) => ({ ...d, questions: d.questions.filter((_, idx) => idx !== i) }));
    const saveSurvey = () => {
        const payload = {
            title: draft.title, description: draft.description,
            questions: draft.questions.map((q) => ({ text: q.text, type: q.type, options: q.type === "single" ? q.optionsStr.split(",").map((o) => o.trim()).filter(Boolean) : [] })),
        };
        axios.post("/api/surveys", payload)
            .then((res) => { notify(res.data.message.en, "success-notification"); setDraft({ title: "", description: "", questions: [{ text: "", type: "single", optionsStr: "" }] }); setView("list"); setUpdates((u) => u + 1); })
            .catch((err) => notify(err.response?.data?.message?.en || "Error", "error-notification"));
    };

    // ---- fill ----
    const [fillData, setFillData] = useState(null);
    const [answers, setAnswers] = useState({});
    const openFill = (id) => {
        axios.get(`/api/surveys/${id}`).then((res) => { setFillData(res.data.data); setAnswers({}); setView("fill"); }).catch(() => {});
    };
    const submitFill = () => {
        const payload = { answers: fillData.questions.map((q) => ({ question_id: q.id, answer_text: answers[q.id] ?? "" })) };
        axios.post(`/api/surveys/${fillData.survey.id}/respond`, payload)
            .then((res) => { notify(res.data.message.en, "success-notification"); setView("list"); setUpdates((u) => u + 1); })
            .catch((err) => notify(err.response?.data?.message?.en || "Error", "error-notification"));
    };

    // ---- results ----
    const [results, setResults] = useState(null);
    const openResults = (id) => {
        axios.get(`/api/surveys/${id}/results`).then((res) => { setResults(res.data.data); setView("results"); }).catch(() => {});
    };

    const toggleActive = (s) => {
        axios.put(`/api/surveys/${s.id}`, { active: s.active ? 0 : 1 })
            .then((res) => { notify(res.data.message.en, "success-notification"); setUpdates((u) => u + 1); })
            .catch((err) => notify(err.response?.data?.message?.en || "Error", "error-notification"));
    };
    const deleteSurvey = (id) => {
        if (!window.confirm(t("survey.confirmDelete"))) return;
        axios.delete(`/api/surveys/${id}`)
            .then((res) => { notify(res.data.message.en, "success-notification"); setUpdates((u) => u + 1); })
            .catch((err) => notify(err.response?.data?.message?.en || "Error", "error-notification"));
    };

    // ============ CREATE VIEW ============
    if (view === "create") {
        return (
            <div className="page-content-main-container">
                <h2 className="page-title">{t("survey.create")}</h2>
                <button className="btn btn-sm btn-outline-secondary" style={{ marginBottom: "14px" }} onClick={() => setView("list")}>← {t("survey.back")}</button>
                <div style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: "12px", padding: "18px", maxWidth: "720px" }}>
                    <div style={{ marginBottom: "10px" }}>
                        <label style={{ fontSize: "12px", opacity: 0.7, display: "block" }}>{t("survey.name")}</label>
                        <input className="form-control" value={draft.title} onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))} />
                    </div>
                    <div style={{ marginBottom: "16px" }}>
                        <label style={{ fontSize: "12px", opacity: 0.7, display: "block" }}>{t("survey.description")}</label>
                        <textarea className="form-control" rows={2} value={draft.description} onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))} />
                    </div>
                    <h4 style={{ fontSize: "14px", marginBottom: "8px" }}>{t("survey.questions")}</h4>
                    {draft.questions.map((q, i) => (
                        <div key={i} style={{ border: "1px solid var(--border)", borderRadius: "10px", padding: "12px", marginBottom: "10px" }}>
                            <div style={{ display: "flex", gap: "8px", marginBottom: "8px" }}>
                                <input className="form-control" style={{ flex: 1 }} placeholder={`${t("survey.question")} ${i + 1}`} value={q.text} onChange={(e) => setQ(i, { text: e.target.value })} />
                                <select className="form-control" style={{ width: "auto" }} value={q.type} onChange={(e) => setQ(i, { type: e.target.value })}>
                                    <option value="single">{t("survey.single")}</option>
                                    <option value="text">{t("survey.text")}</option>
                                </select>
                                {draft.questions.length > 1 && <button className="btn btn-sm btn-outline-danger" onClick={() => removeQ(i)}>✕</button>}
                            </div>
                            {q.type === "single" && (
                                <input className="form-control" placeholder={t("survey.options")} value={q.optionsStr} onChange={(e) => setQ(i, { optionsStr: e.target.value })} />
                            )}
                        </div>
                    ))}
                    <button className="btn btn-sm btn-outline-secondary" style={{ marginBottom: "16px" }} onClick={addQuestion}>+ {t("survey.addQuestion")}</button>
                    <div><button className="btn btn-primary" onClick={saveSurvey}>{t("survey.save")}</button></div>
                </div>
            </div>
        );
    }

    // ============ FILL VIEW ============
    if (view === "fill" && fillData) {
        return (
            <div className="page-content-main-container">
                <h2 className="page-title">{fillData.survey.title}</h2>
                <button className="btn btn-sm btn-outline-secondary" style={{ marginBottom: "14px" }} onClick={() => setView("list")}>← {t("survey.back")}</button>
                <div style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: "12px", padding: "18px", maxWidth: "640px" }}>
                    {fillData.survey.description && <p style={{ opacity: 0.75, marginBottom: "16px" }}>{fillData.survey.description}</p>}
                    {fillData.answered ? (
                        <div style={{ opacity: 0.7 }}>{t("survey.submitted")}</div>
                    ) : (
                        <>
                            {fillData.questions.map((q) => (
                                <div key={q.id} style={{ marginBottom: "16px" }}>
                                    <div style={{ fontWeight: 600, marginBottom: "6px" }}>{q.text}</div>
                                    {q.type === "single" ? (
                                        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                                            {q.options.map((o, oi) => (
                                                <label key={oi} style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
                                                    <input type="radio" name={`q${q.id}`} checked={answers[q.id] === o} onChange={() => setAnswers((a) => ({ ...a, [q.id]: o }))} />
                                                    <span>{o}</span>
                                                </label>
                                            ))}
                                        </div>
                                    ) : (
                                        <input className="form-control" placeholder={t("survey.yourAnswer")} value={answers[q.id] || ""} onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: e.target.value }))} />
                                    )}
                                </div>
                            ))}
                            <button className="btn btn-primary" onClick={submitFill}>{t("survey.submit")}</button>
                        </>
                    )}
                </div>
            </div>
        );
    }

    // ============ RESULTS VIEW ============
    if (view === "results" && results) {
        return (
            <div className="page-content-main-container">
                <h2 className="page-title">{results.survey.title} — {t("survey.results")}</h2>
                <button className="btn btn-sm btn-outline-secondary" style={{ marginBottom: "14px" }} onClick={() => setView("list")}>← {t("survey.back")}</button>
                <div style={{ marginBottom: "16px", fontSize: "14px" }}>{t("survey.totalResponses")}: <strong>{results.total}</strong></div>
                {results.total === 0 ? <div style={{ opacity: 0.6 }}>{t("survey.noResponses")}</div> : results.questions.map((q) => (
                    <div key={q.id} style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: "12px", padding: "16px", marginBottom: "12px", maxWidth: "640px" }}>
                        <div style={{ fontWeight: 600, marginBottom: "10px" }}>{q.text}</div>
                        {q.type === "single" ? q.counts.map((c, ci) => {
                            const pct = results.total > 0 ? Math.round((c.count / results.total) * 100) : 0;
                            return (
                                <div key={ci} style={{ marginBottom: "8px" }}>
                                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", marginBottom: "2px" }}><span>{c.option}</span><span>{c.count} ({pct}%)</span></div>
                                    <div style={{ background: "var(--surface-2, rgba(127,127,127,0.15))", borderRadius: "6px", height: "8px" }}>
                                        <div style={{ width: `${pct}%`, background: "#2f80ed", height: "100%", borderRadius: "6px" }} />
                                    </div>
                                </div>
                            );
                        }) : (
                            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                                {q.answers.length === 0 ? <span style={{ opacity: 0.5, fontSize: "13px" }}>—</span> : q.answers.map((a, ai) => (
                                    <div key={ai} style={{ background: "var(--surface-2, rgba(127,127,127,0.1))", borderRadius: "8px", padding: "6px 10px", fontSize: "13px" }}>{a}</div>
                                ))}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        );
    }

    // ============ LIST VIEW ============
    return (
        <div className="page-content-main-container">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "10px" }}>
                <h2 className="page-title" style={{ margin: 0 }}>{t("survey.title")}</h2>
                {isAdmin && <button className="btn btn-primary" onClick={() => setView("create")}>+ {t("survey.create")}</button>}
            </div>

            {surveys.length === 0 ? (
                <div style={{ opacity: 0.6, marginTop: "16px" }}>{t("survey.none")}</div>
            ) : (
                <div style={{ marginTop: "16px", display: "flex", flexDirection: "column", gap: "10px" }}>
                    {surveys.map((s) => (
                        <div key={s.id} style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: "12px", padding: "16px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "10px" }}>
                            <div>
                                <div style={{ fontWeight: 600 }}>{s.title} {!s.active && <span style={{ fontSize: "11px", opacity: 0.6 }}>· {t("survey.closed")}</span>}</div>
                                {s.description && <div style={{ fontSize: "13px", opacity: 0.7 }}>{s.description}</div>}
                                <div style={{ fontSize: "12px", opacity: 0.5, marginTop: "3px" }}>
                                    {isStaff ? `${t("survey.responses")}: ${s.responses}` : (Number(s.answered) > 0 ? t("survey.answered") : "")}
                                    {s.created_at ? ` · ${core.formdatDate(s.created_at, "dd-mm-yyyy")}` : ""}
                                </div>
                            </div>
                            <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                                {!isStaff && Number(s.answered) === 0 && <button className="btn btn-sm btn-primary" onClick={() => openFill(s.id)}>{t("survey.fill")}</button>}
                                {isStaff && <button className="btn btn-sm btn-outline-secondary" onClick={() => openResults(s.id)}>{t("survey.viewResults")}</button>}
                                {isAdmin && <button className="btn btn-sm btn-outline-secondary" onClick={() => toggleActive(s)}>{s.active ? t("survey.close") : t("survey.reopen")}</button>}
                                {isAdmin && <button className="btn btn-sm btn-outline-danger" onClick={() => deleteSurvey(s.id)}>{t("survey.delete")}</button>}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default Surveys;
